// Styles
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'

// Vuetify
import { createVuetify } from 'vuetify'

/**
 * Lyt-ning Theme Configuration
 *
 * The application supports two themes: light and dark
 * Both themes use the Lyt-ning color scheme for a lightning-fast POS experience
 *
 * Color Usage:
 * - primary: Electric Blue (#339CFF) - Main brand color (buttons, links, key UI elements)
 * - secondary: Neon Purple (#A529FF) - Supporting brand color (accents, secondary actions)
 * - accent: Electric Blue - Highlight color for special promotions
 * - success: #32D74B - Successful actions (added to cart, order placed)
 * - warning: #FF9500 - Warnings and cautions (low stock, payment pending)
 * - error: #FF3B30 - Error states (out of stock, failed operations)
 * - info: Electric Blue - Informational messages
 * - background: Main page background
 * - surface: Card and component backgrounds
 */

const lightTheme = {
  dark: false,
  colors: {
    // Lyt-ning Brand colors - Electric Blue & Neon Purple scheme
    primary: '#339CFF',        // Electric Blue - Main brand color
    secondary: '#A529FF',      // Neon Purple - Secondary brand color
    accent: '#339CFF',         // Electric Blue for accents

    // Status colors - Lyt-ning palette
    success: '#32D74B',        // Green for success states
    warning: '#FF9500',        // Orange for warnings
    error: '#FF3B30',          // Red for errors
    info: '#339CFF',           // Electric Blue for info messages

    // Surface colors - Light mode
    background: '#FFFFFF',     // White background
    surface: '#F8FAFC',        // Light card background
    'surface-bright': '#FFFFFF', // Bright surface
    'surface-variant': '#F0F4F8', // Subtle gradient background

    // Text colors (explicit for better control) - Light mode
    'on-primary': '#FFFFFF',   // Text on primary color
    'on-secondary': '#FFFFFF', // Text on secondary color
    'on-background': '#0F172A', // Main text color - Dark slate
    'on-surface': '#0F172A',   // Text on surfaces
    'on-surface-variant': '#64748B', // Muted text - Slate
    'on-success': '#FFFFFF',   // Text on success color
    'on-warning': '#000000',   // Text on warning color (better contrast)
    'on-error': '#FFFFFF',     // Text on error color
    'on-info': '#FFFFFF',      // Text on info color
  }
}

const darkTheme = {
  dark: true,
  colors: {
    // Lyt-ning Brand colors - Dark mode with gradient backgrounds
    primary: '#339CFF',        // Electric Blue - Maintains consistency
    secondary: '#A529FF',      // Neon Purple - Maintains consistency
    accent: '#339CFF',         // Electric Blue for accents

    // Status colors - Lyt-ning palette (consistent across modes)
    success: '#32D74B',        // Green for success states
    warning: '#FF9500',        // Orange for warnings
    error: '#FF3B30',          // Red for errors
    info: '#339CFF',           // Electric Blue for info messages

    // Surface colors - Dark mode with Lyt-ning dark theme
    // Use a SOLID color token; the actual page gradient is applied via CSS (body background)
    background: '#0D0B33',  // Top color of gradient; prevents components from trying to use gradients as plain colors
    surface: '#1A1840',        // Dark purple-tinted cards
    'surface-bright': '#252055', // Brighter surface for emphasis
    'surface-variant': '#212145', // Slightly different shade for alternating sections

    // Text colors (explicit for better control) - Dark mode
    'on-primary': '#FFFFFF',   // Text on primary color (white on blue)
    'on-secondary': '#FFFFFF', // Text on secondary color (white on purple)
    'on-background': '#FFFFFF', // Main text color - White
    'on-surface': '#FFFFFF',   // Text on surfaces - White
    'on-surface-variant': '#A0A0A0', // Muted text - Light gray
    'on-success': '#FFFFFF',   // Text on success color
    'on-warning': '#000000',   // Text on warning color (better contrast)
    'on-error': '#FFFFFF',     // Text on error color
    'on-info': '#FFFFFF',      // Text on info color
  }
}

export default createVuetify({
  theme: {
    defaultTheme: 'light',
    themes: {
      light: lightTheme,
      dark: darkTheme,
    },
    variations: {
      colors: ['primary', 'secondary', 'accent'],
      lighten: 2,
      darken: 2,
    },
  },
  defaults: {
    // Set default props for Vuetify components
    VBtn: {
      style: 'text-transform: none;', // Remove uppercase transformation
      rounded: 'lg',
    },
    VCard: {
      elevation: 1,
      rounded: 'lg',
    },
    VTextField: {
      variant: 'outlined',
      density: 'comfortable',
    },
    VSelect: {
      variant: 'outlined',
      density: 'comfortable',
    },
  },
})
